import { ref, computed, watch } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useStore } from 'vuex';
import TransitionSlide from 'components/transitions/slide-transition';
import { isEqual, isArray } from 'lodash';
import i18n from 'src/assets/i18n/i18n';
import { MdIconSubmissions, MdTooltip } from '@antiqua-dls/vue-components';

import ICONS from '../../../constants/icons';
import ArrowIcon from '../../../icons/arrow-icon';
import generalIcon from '../../../icons/general-icon';
import ROUTES from '../../../constants/app-routes';
import { getImpersonated } from '../../../script/helper';
import { getNavLockLevel } from 'src/utils/helper/contractTermination';
import { NAV_LOCK_LEVEL } from 'src/utils/constants/contract-termination';

export default {
    components: {
        generalIcon,
        TransitionSlide,
        ArrowIcon,
        MdIconSubmissions,
        MdTooltip
    },

    props: {
        sideNavTab: {
            type: Object
        },
        index: {
            type: Number
        },
        showHamIcon: {
            type: Boolean
        }
    },

    setup(props, { emit }) {
        const { t } = i18n().global
        const store = useStore()
        const showChild = ref(null)
        const disabledAccordionOpen = ref(false)
        const router = useRouter()
        const route = useRoute()
        const hasRole = computed(() => store.getters['auth/hasRole'])
        const user = computed(() => store.getters['auth/user'])
        const isGracePeriodExpired = computed(() => store.getters['auth/isGracePeriodExpired']);

        const getUfda2Status = (title) => {
            if (t(title) === 'Articles' || t(title) === 'Videos' || t(title) === 'Events') {
                const userFoundries = JSON.parse(localStorage.getItem('userFoundries'))
                const currentFoundryId = localStorage.getItem('sessionFoundryId')
                const selectedFoundry = userFoundries.find(foundry => isEqual(foundry.id, currentFoundryId))
                const hasUfda2 = isEqual(selectedFoundry.contractSummary, 'UFDA 2')
                return hasUfda2
            }
            return true
        }
        /** changing the route for dashboard for foundry support and foundryuser */
        const changeRoute = (navTab, index, title) => {
            if (!navTab.childRoutes) {
                store.commit('sideNavigation/changeHamIconStatus', false)
                if (hasRole.value(['foundrySupport']) && navTab.name === 'Dashboard' && !getImpersonateUser.value) {
                    router.push(ROUTES.MANAGE_FONTS.path)
                } else if (navTab.uri.startsWith('https')) {
                    getUfda2Status(title) ? window.open(navTab.uri, '_blank') : router.push(navTab.uriNonUfda2)
                } else {
                    router.push(navTab.uri)
                }
            }
            if (!index) return;
            emit('update:sideNavTab', title)
        }
        /** checking the tab active */
        const isActive = () => {
            const currentRouteName = route?.name
            const supportNonDashboardTabs = ['Applications', 'Promotions', 'Impersonate', 'Inventory Hub', 'DropShip', 'MFP Numbers', 'Quarterly Earnings Upload']
            const supportActiveDashboard = hasRole.value(['foundrySupport']) && isEqual(currentRouteName, ROUTES.MANAGE_FONTS.name) && !supportNonDashboardTabs.includes(props.sideNavTab.name) && !getImpersonateUser.value
            if (hasRole.value(['foundrySupport', 'bulkIngestionUser']) || hasRole.value(['FS-Impersonate']) && props.sideNavTab.name === 'Dashboard') {
                return (supportActiveDashboard || props.sideNavTab.relatedRouteName.includes(currentRouteName))
            }
            return (supportActiveDashboard || props.sideNavTab.linkedRoutes.includes(currentRouteName))
        }

        /** Toggle the parent div show or hide */
        const getParentClass = () => {
            const hasChildRoutes = props.sideNavTab?.childRoutes
            const activeClass = isActive() ? 'fou-sidenav__item--active' : '' // Add 'fou-sidenav__item--active' class if isActive() is true
            const openClass = props.sideNavTab.isOpen ? 'open' : '' // Add 'open' class if showChild.value is true
            if (hasChildRoutes) {
                return `fou-sidenav__item--haschild ${activeClass} ${openClass}` // combine class for parent tab having child
            }
            return `${activeClass}`
        }

        /** add active class to the tab for all of the linked routes */
        const chilClass = nestRoute => {
            const currentRouteName = route.name
            if (route?.params?.id === t(nestRoute.title).toLowerCase()) {
                return 'fou-sidenav__item__link--child--active'
            }
            if (nestRoute.linkedRoutes.includes(currentRouteName) && !nestRoute.uri.startsWith('https')) {
                return 'fou-sidenav__item__link--child--active'
            }
        }

        const iconColor = computed(() => {
            const currentRouteName = typeof route.name === 'string' ? route.name : ''
            let linkedRoutes = ''
            if (hasRole.value(['foundrySupport', 'bulkIngestionUser']) || hasRole.value(['FS-Impersonate']) && props.sideNavTab.name === 'Dashboard') {
                linkedRoutes = props.sideNavTab.relatedRouteName
            } else {
                linkedRoutes = props.sideNavTab.linkedRoutes
            }
            if (hasRole.value(['foundrySupport']) && props.sideNavTab.name === 'Dashboard' && currentRouteName === 'home') {
                return ICONS.ACTIVE_COLOR
            }
            return linkedRoutes.includes(currentRouteName) ? ICONS.ACTIVE_COLOR : ICONS.INACTIVE_COLOR
        })

        const parentClass = computed(() => getParentClass())
        const getImpersonateUser = computed(() => {
            return getImpersonated(user.value)
        })

        const isChildSelected = computed(() => {
            return parentClass.value.includes('fou-sidenav__item--haschild fou-sidenav__item--active')
        })

        const showArrow = computed(() => props.sideNavTab.childRoutes)

        const iconFillColor = computed(() => props.sideNavTab.icon.fill ? iconColor.value : null)

        const iconStrokeColor = computed(() => props.sideNavTab.icon.stroke ? iconColor.value : null)

        const dataId = (title, type = '') => {
            const tabTd = title.replace(' ', '-').toLowerCase()
            return `sidenavigation-${tabTd}-${type}`
        }

        const getPermissionsArr = computed(() => store.getters['auth/getPermissionsArr'])

        const getNavigationTabsPermission = (tabPermission) => {
            let permission = []
            if (getImpersonateUser.value) {
                permission = JSON.parse(window.localStorage.getItem('permissionsArr')) ?? []
            } else {
                permission = getPermissionsArr.value
            }
            if (isArray(tabPermission) && tabPermission.length > 0) {
                return tabPermission.some((p) => (permission ?? []).includes(p))
            }
            return (permission ?? []).includes(tabPermission)
        }

        const ctIsUserAdmin = computed(() => store.getters['auth/isUserAdmin'])
        const ctFoundriesTerminated = computed(() => store.getters['auth/foundriesTerminated'])

        const navLockLevel = computed(() => {
            return getNavLockLevel({
                foundriesTerminated: ctFoundriesTerminated.value,
                isUserAdmin: ctIsUserAdmin.value
            })
        })

        const isContractNavLock = computed(() => {
            if (hasRole.value(['foundrySupport'])) return false
            if (navLockLevel.value === NAV_LOCK_LEVEL.FULL) return props.sideNavTab.name === 'Resources' ? false : true
            if (navLockLevel.value === NAV_LOCK_LEVEL.PARTIAL) return !!props.sideNavTab.disabledIfContractTermination
            return false
        })

        const isEmailVerifyNavLock = computed(() => {
            if (hasRole.value(['foundrySupport'])) return false
            return isGracePeriodExpired.value && !!props.sideNavTab.disabledIfGracePeriodExpired
        })

        const isNavDisabled = computed(() => isContractNavLock.value || isEmailVerifyNavLock.value)

        const isNavLockActive = computed(() => isNavDisabled.value)

        const navLockTooltipKey = computed(() => {
            if (isContractNavLock.value) return 'components.general.contract_termination.tooltip.label'
            if (isEmailVerifyNavLock.value) return 'components.general.verify_email.tooltip.label'
            return null
        })

        const visibleChildRoutes = computed(() => {
            const cr = props.sideNavTab?.childRoutes
            if (!cr?.length) {
                return []
            }
            return cr.filter(
                (nestRoute) =>
                    nestRoute.copyParentPermission
                    || getNavigationTabsPermission(nestRoute.tabPermission)
            )
        })

        const showChildNavList = computed(() => {
            if (!visibleChildRoutes.value.length) {
                return false
            }
            if (isNavLockActive.value) {
                return disabledAccordionOpen.value
            }
            return (props.sideNavTab.isOpen && props.sideNavTab.childRoutes) || isChildSelected.value
        })
       
        const verifyEmailTooltipPlacement = computed(() => {
            if(props.showHamIcon) return 'top';
             /** First rows sit under the fixed app bar — open the tooltip downward; others use top. Tweak count if needed. */
            return (props.index ?? 0) < 3 ? 'bottom-start' : 'top';
        });

        const verifyEmailTooltipProps = computed(() => ({
            trigger: 'hover',
            padding: 'medium',
            hidedelay: '0',
            placement: verifyEmailTooltipPlacement.value
        }))

        const getChildItemClass = (nestRoute) => {
            const ac = chilClass(nestRoute)
            if (isNavLockActive.value) {
                return ac
                    ? [
                        'fou-sidenav__item__link',
                        'fou-sidenav__item__link--child',
                        'fou-sidenav__item__link--child-standalone-indent',
                        'disabled',
                        ac
                    ]
                    : [
                        'fou-sidenav__item__link',
                        'fou-sidenav__item__link--child',
                        'fou-sidenav__item__link--child-standalone-indent',
                        'disabled'
                    ]
            }
            return ac
                ? ['fou-sidenav__item__link', 'fou-sidenav__item__link--child', ac]
                : ['fou-sidenav__item__link', 'fou-sidenav__item__link--child']
        }

        const onChildSelect = (nestRoute) => {
            if (!nestRoute.uri || nestRoute.uri === '#') return // placeholder tab
            changeRoute(nestRoute, false, nestRoute.title)
        }

        const toggleDisabledAccordion = () => {
            disabledAccordionOpen.value = !disabledAccordionOpen.value
            if (disabledAccordionOpen.value) {
                emit('update:sideNavTab', props.sideNavTab.title)
            }
        }

        watch(() => props.sideNavTab.isOpen, (newVal) => {
            if (!newVal && disabledAccordionOpen.value) {
                disabledAccordionOpen.value = false
            }
        })

        return {
            getUfda2Status,
            changeRoute,
            isActive,
            getParentClass,
            chilClass,
            hasRole,
            user,
            iconColor,
            parentClass,
            getImpersonateUser,
            showArrow,
            showChild,
            iconFillColor,
            iconStrokeColor,
            dataId,
            getPermissionsArr,
            getNavigationTabsPermission,
            isChildSelected,
            isNavDisabled,
            isContractNavLock,
            isEmailVerifyNavLock,
            isNavLockActive,
            navLockTooltipKey,
            visibleChildRoutes,
            showChildNavList,
            verifyEmailTooltipPlacement,
            verifyEmailTooltipProps,
            getChildItemClass,
            onChildSelect,
            disabledAccordionOpen,
            toggleDisabledAccordion
        }
    }
}
